<template>
  <div>
    <provisioning-status @refresh="load" />
    <v-card class="main-card">
      <v-card-title class="screen-title">
        <span>SaaS利用一覧</span>
        <manual-button path="/ms-service-list/" />
      </v-card-title>
      <v-card-text class="pa-2" v-if="isLoaded">
        <v-row no-gutters>
          <v-col>
            <app-search :search="saas.search" v-model="saas.filter" />
          </v-col>
          <div class="d-flex flex-row ga-1 flex-wrap ml-2">
            <v-btn color="primary" @click="addSaasService" v-permission.admin
              >追加</v-btn
            >
            <v-btn color="primary" @click="downloadCsv">一覧ダウンロード</v-btn>
          </div>
        </v-row>
        <div class="d-flex flex-row justify-end mb-2">
          <v-btn color="primary" @click="load">一覧更新</v-btn>
        </div>
        <app-table
          v-model:table-sort-by="saas.sortBy"
          primary-key="saasCommon.vpnVnCode"
          :fields="saas.fields"
          :items="saas.items"
          :filter-option="saas.filter_option"
          :filter="saas.filter"
          v-model="saas.selected"
          :hover="true"
          sticky-header="50vh"
          @click:row="serviceDetails"
        >
          <template #cell(vnName)="data">
            {{ $filter.emptyTo(data.item.saasCommon.vnName, "-") }}
          </template>
          <template #cell(serviceName)="data">
            {{ data.item.msServiceName }}<br />
            {{ data.item.sfdcServiceName }}
          </template>
          <template #cell(usage)="data">
            {{ data.item.msPeeringFlag }}<br />
            {{ data.item.sfdcFlag }}
          </template>
          <template #cell(upperLimitNapt)="data">
            {{
              data.item.msPeeringInfo && data.item.msPeeringInfo.msPeering
                ? data.item.msPeeringInfo.msPeering.upperLimitNapt
                : "-"
            }}<br />
            {{
              data.item.sfdcInfo && data.item.sfdcInfo.sfdc
                ? data.item.sfdcInfo.sfdc.upperLimitNapt
                : "-"
            }}
          </template>
          <template #cell(upperLimitNat)="data">
            {{
              data.item.msPeeringInfo && data.item.msPeeringInfo.msPeering
                ? data.item.msPeeringInfo.msPeering.upperLimitNat
                : "-"
            }}
            <br />
            {{ "-" }}
          </template>
        </app-table>
      </v-card-text>
    </v-card>
  </div>
</template>

<script lang="ts">
import type { SaaSServiceListEntity } from "@/apis/SaasApi";
import type {
  AppTableHeader,
  FilterOption,
  SortItem,
} from "@/components/AppTable.vue";
import SaaSServiceUsageReference from "@/modals/saas/SaaSServiceUsageReference.vue";
import SaaSServiceUsageEdit from "@/modals/saas/SaaSServiceUsageEdit.vue";
import { defineComponent } from "vue";
import type { SearchOption } from "@/components/AppSearch.vue";

export type SaaSListEntity = SaaSServiceListEntity & {
  msServiceName: "Microsoft Peering";
  sfdcServiceName: "Salesforce";
  msPeeringFlag: "利用する" | "利用しない";
  sfdcFlag: "利用する" | "利用しない";
};

type SaaSFieldType = SaaSServiceListEntity & {
  serviceName: "Microsoft Peering" | "Salesforce";
  usage: "利用する" | "利用しない";
  upperLimitNapt: number | "";
  upperLimitNat: number | "";
};

export default defineComponent({
  name: "SaaSServiceApplicationList",
  data() {
    return {
      saas: {
        sortBy: {
          key: "vpnVnCode",
          order: "asc",
        } as SortItem,
        fields: [
          {
            key: "vpnVnCode",
            title: "VPN/VNコード",
            sortable: true,
            sortByFormatted: true,
            filterByFormatted: true,
            width: "250px",
            formatter: (_v: any, _k: any, item: SaaSListEntity) => {
              return item.saasCommon.vpnVnCode;
            },
          },
          {
            key: "vnName",
            title: "VNネットワーク名",
            sortable: true,
            sortByFormatted: true,
            filterByFormatted: true,
            width: "250px",
            formatter: (_v: any, _k: any, item: SaaSListEntity) => {
              return item.saasCommon.vnName;
            },
          },
          {
            key: "serviceName",
            title: "利用サービス",
            width: "250px",
          },
          {
            key: "usage",
            title: "利用状況",
            width: "200px",
          },
          {
            key: "upperLimitNapt",
            title: "NAPTアドレス上限数",
            align: "end",
            width: "200px",
          },
          {
            key: "upperLimitNat",
            title: "NATアドレス上限数",
            align: "end",
            width: "200px",
          },
        ] as AppTableHeader<SaaSFieldType>[],
        items: [] as SaaSListEntity[],
        selected: [] as SaaSListEntity[],
        search: {
          vpnVnCode: {
            type: "text",
            label: "VPN/VNコード",
          },
          vnName: {
            type: "text",
            label: "VNネットワーク名",
          },
          msPeeringFlag: {
            type: "select",
            label: "Microsoft Peering",
            items: usageItems,
          },
          sfdcFlag: {
            type: "select",
            label: "Salesforce",
            items: usageItems,
          },
        } as SearchOption,
        filter: {} as Record<string, unknown>,
        filter_option: {
          vpnVnCode: "like",
          vnName: "like",
          msPeeringFlag: "equal",
          sfdcFlag: "equal",
        } as FilterOption,
      },
      keyId: "",
      usageItems: usageItems,
      isLoaded: false,
    };
  },
  async mounted() {
    await this.load();
    this.isLoaded = true;
  },
  methods: {
    async load() {
      /** SaaS利用情報APIの呼び出し処理 */
      const data = await this.$api.saas.getAll();
      this.saas.items = data.saasServiceList.map((e: any) => ({
        ...e,
        msServiceName: "Microsoft Peering",
        sfdcServiceName: "Salesforce",
        msPeeringFlag: e.msPeeringInfo?.msPeering ? "利用する" : "利用しない",
        sfdcFlag: e.sfdcInfo?.sfdc ? "利用する" : "利用しない",
      }));
      this.keyId = data.saasKeyId;
    },

    /** サービス追加画面表示 */
    async addSaasService() {
      await this.$modal.show(SaaSServiceUsageEdit, {
        saasKeyId: this.keyId,
        // 一覧項目に既に存在しているVpnVnCodeを設定する
        existVpnVnCodeList: this.saas.items.map((v) => v.saasCommon.vpnVnCode),
      });
      await this.load();
    },

    /** サービス詳細情報画面表示 */
    async serviceDetails(item: SaaSListEntity) {
      await this.$modal.show(SaaSServiceUsageReference, {
        saas: item,
        saasKeyId: this.keyId,
      });
      await this.load();
    },
    /** フロント側のみでのCSVダウンロード */
    downloadCsv() {
      // ダウンロード用データを編集する
      const csvDataArray: SaaSFieldType[] = [];
      this.saas.items
        .sortBy([(e) => e.saasCommon.vpnVnCode, "asc"])
        .map((v) => {
          // Microsoft Peeringのデータを編集する
          csvDataArray.push({
            ...v,
            serviceName: v.msServiceName,
            usage: v.msPeeringFlag,
            upperLimitNapt: v.msPeeringInfo?.msPeering?.upperLimitNapt ?? "",
            upperLimitNat: v.msPeeringInfo?.msPeering?.upperLimitNat ?? "",
          });
          // Salesforceのデータを編集する
          csvDataArray.push({
            ...v,
            serviceName: v.sfdcServiceName,
            usage: v.sfdcFlag,
            upperLimitNapt: v.sfdcInfo?.sfdc?.upperLimitNapt ?? "",
            upperLimitNat: "",
          });
        });
      // csvをダウンロードする
      this.$service.csvService.appTableCsvDownLoad(
        "saas_application_status",
        csvDataArray,
        this.$service.csvService.genFieldList(this.saas.fields)
      );
    },
  },
});

/** Microsoft Peering/Salesforce表示マッピング用 */
export const usageItems = [
  { value: "利用する", text: "利用する" },
  { value: "利用しない", text: "利用しない" },
];
</script>
